import React from "react";
import { TaskStatusBadge } from "./task-status-badge";
import { safe_get } from "../../../report-visuals";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "../task-list-components/CircularProgress";
import { useStore } from "../../../helpers";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { toJS } from "mobx";

interface TaskCardProps {
    task: any;
    onClick: () => void;
    onEdit: () => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({ task, onClick, onEdit }) => {
    const { t } = useTranslation("task_manager");
    const rootStore = useStore();
    const { taskManagerStore } = rootStore;
    const { projectid } = useParams();
    const taskId = task?.id;

    const taskName = safe_get(task, "more.name", "N/A");
    const region = safe_get(task, "node.piv_level1", "/");
    const assignedUser = safe_get(task, "user", null);
    const interviewsCount = safe_get(task, "count.count", 0);
    const fieldWorkProgress = safe_get(task, "status.progress_field_work", 0);
    const qaProgress = safe_get(task, "status.progress_qa", 0);
    const isAssigned = safe_get(task, "assigned", false);

    const handleClone = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!taskId) return;

        try {
            const res = await taskManagerStore.clone_task(taskId);
            if (res?.status === 200 || res?.status === 204) {
                toast.success("Task cloned successfully");
                await refreshTaskList();
            } else {
                toast.error("Failed to clone task");
            }
        } catch (error) {
            toast.error("Error cloning task");
            console.error(error);
        }
    };

    const refreshTaskList = async () => {
        // Invalidate cache and refresh task list
        await taskManagerStore.get_task_list(projectid);
    };

    const handleSync = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!taskId) return;

        try {
            const res = await taskManagerStore.refresh_user_tasks(taskId);
            if (res?.status === 200 || res?.status === 204) {
                toast.success("Task synced successfully");
                await refreshTaskList();
            } else {
                toast.error("Failed to sync task");
            }
        } catch (error) {
            toast.error("Error syncing task");
            console.error(error);
        }
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!taskId) return;

        if (!window.confirm("Are you sure you want to delete this task?")) {
            return;
        }

        try {
            const res = await taskManagerStore.delete_task(taskId);
            if (res?.status === 200 || res?.status === 204) {
                toast.success("Task deleted successfully");
                await refreshTaskList();
            } else {
                toast.error("Failed to delete task");
            }
        } catch (error) {
            toast.error("Error deleting task");
            console.error(error);
        }
    };

    const handleShowMap = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!task) return;

        // Set active task, close task view if open, and show map
        taskManagerStore.set_active_task(task);
        await taskManagerStore.set_task_view_modal(false);
        await taskManagerStore.update_show_booth_map(true);
    };

    // Determine status based on progress
    const getStatus = () => {
        if (fieldWorkProgress === 100 && qaProgress === 100) return "completed";
        if (fieldWorkProgress > 0 || qaProgress > 0) return "in-progress";
        return "not-started";
    };

    return (
        <div
            className="group relative bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border border-gray-200 hover:border-gray-300"
            onClick={onClick}
        >
            {/* Status Badge - Prominent at top */}
            <div className="p-4 pb-0">
                <TaskStatusBadge status={getStatus()} />
            </div>

            {/* Action Buttons - Show on hover */}
            <div className="absolute top-2 right-2 flex flex-wrap gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 max-w-[140px]">
                <button
                    onClick={async (e) => {
                        e.stopPropagation();
                        onClick();
                        // Refresh list after viewing (in case data changed)
                        await refreshTaskList();
                    }}
                    className="bg-white hover:bg-blue-50 shadow-md rounded-full w-9 h-9 flex items-center justify-center transition-colors"
                    title="View Task"
                >
                    <i className="fas fa-eye text-blue-600 text-sm" />
                </button>

                <button
                    onClick={async (e) => {
                        e.stopPropagation();
                        onEdit();
                        // Refresh list after editing
                        await refreshTaskList();
                    }}
                    className="bg-white hover:bg-green-50 shadow-md rounded-full w-9 h-9 flex items-center justify-center transition-colors"
                    title="Edit Task"
                >
                    <i className="fas fa-edit text-green-600 text-sm" />
                </button>

                <button
                    onClick={handleClone}
                    className="bg-white hover:bg-purple-50 shadow-md rounded-full w-9 h-9 flex items-center justify-center transition-colors"
                    title="Clone Task"
                >
                    <i className="fas fa-copy text-purple-600 text-sm" />
                </button>

                <button
                    onClick={handleSync}
                    className="bg-white hover:bg-amber-50 shadow-md rounded-full w-9 h-9 flex items-center justify-center transition-colors"
                    title="Sync Task"
                >
                    <i className="fas fa-sync-alt text-amber-600 text-sm" />
                </button>

                <button
                    onClick={handleDelete}
                    className="bg-white hover:bg-red-50 shadow-md rounded-full w-9 h-9 flex items-center justify-center transition-colors"
                    title="Delete Task"
                >
                    <i className="fas fa-trash text-red-600 text-sm" />
                </button>

                <button
                    onClick={handleShowMap}
                    className="bg-white hover:bg-indigo-50 shadow-md rounded-full w-9 h-9 flex items-center justify-center transition-colors"
                    title="Show Map"
                >
                    <i className="fas fa-map-marked-alt text-indigo-600 text-sm" />
                </button>
            </div>

            {/* Card Content */}
            <div className="p-4 space-y-3">
                {/* Task Name */}
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 line-clamp-2">
                        {taskName}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">Region: {region}</p>
                </div>

                {/* Assigned To */}
                <div className="flex items-center space-x-2">
                    {isAssigned && assignedUser ? (
                        <>
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold text-sm">
                                {assignedUser.name?.charAt(0)?.toUpperCase() || "?"}
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500">Assigned To</p>
                                <p className="text-sm font-semibold text-gray-900 truncate">
                                    {assignedUser.name}
                                </p>
                                {assignedUser.phonenum && (
                                    <p className="text-xs text-gray-500">{assignedUser.phonenum}</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                                <i className="fas fa-user-slash text-gray-400 text-xs" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-xs text-gray-500">Assigned To</p>
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                                    {t("unassigned")}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                {/* Interviews Count */}
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                        <i className="fas fa-clipboard-list text-gray-400" />
                        <p className="text-xs text-gray-500">Interviews</p>
                    </div>
                    <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-50 text-blue-700">
                        {interviewsCount}
                    </div>
                </div>

                {/* Progress Section */}
                <div className="pt-2 border-t border-gray-100">
                    {/* Circular Progress Indicators */}
                    <div className="flex items-center justify-around mb-3">
                        <div className="flex flex-col items-center">
                            <CircularProgress
                                progress={fieldWorkProgress}
                                color="#10B981"
                                size={56}
                                strokeWidth={5}
                            />
                            <span className="text-xs font-medium text-gray-600 mt-1">Field Work</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <CircularProgress
                                progress={qaProgress}
                                color="#F59E0B"
                                size={56}
                                strokeWidth={5}
                            />
                            <span className="text-xs font-medium text-gray-600 mt-1">QC Check</span>
                        </div>
                    </div>

                    {/* Interview Status */}
                    <div className="flex items-center justify-center gap-4 pt-2 border-t border-gray-100">
                        <div className="flex flex-col items-center">
                            <i className="fas fa-circle-check text-green-500 text-base mb-1" />
                            <span className="text-xs font-semibold text-gray-700">{safe_get(task, "status.by_qastatus.valid", 0)}</span>
                            <span className="text-xs text-gray-500">Valid</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <i className="fas fa-circle-xmark text-red-500 text-base mb-1" />
                            <span className="text-xs font-semibold text-gray-700">{safe_get(task, "status.by_qastatus.invalid", 0)}</span>
                            <span className="text-xs text-gray-500">Invalid</span>
                        </div>
                        <div className="flex flex-col items-center">
                            <i className="fas fa-circle-exclamation text-yellow-500 text-base mb-1" />
                            <span className="text-xs font-semibold text-gray-700">{safe_get(task, "status.by_qastatus.not-checked", 0)}</span>
                            <span className="text-xs text-gray-500">Pending</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

